import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ChangeEvent, useCallback, useState } from "react";
import LoadingButton from "src/components/Buttons/LoadingButton/LoadingButton";
import { desktopSx } from "src/utils/styles/modalStyles";
import { Close } from "@mui/icons-material";
import { AlertTitle, FormControl, MenuItem, TextField } from "@mui/material";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { useBulkUpdateOrdersMutation } from "src/api/services/ordersApi";
import DatePicker from "src/components/DatePicker";
import { isOrderStatus, OrderStatus } from "src/api/types";
import { orderStatuses } from "src/constants/orderStatuses";

interface Props {
  open: boolean;
  onClose: () => void;
}

const UpdateOrdersModal = ({ open, onClose }: Props) => {
  const [error, setError] = useState("");
  const [nextAttempt, setNextAttempt] = useState<Date | null>(null);
  const { selected, setSelected } = useSelection();
  const [status, setStatus] = useState<OrderStatus | undefined>(undefined);
  const [bulkUpdate, { isLoading }] = useBulkUpdateOrdersMutation();

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newStatus = event.target.value;
    if (isOrderStatus(newStatus)) {
      setStatus(newStatus);
    }
  }, []);

  const handleCancel = useCallback(() => {
    bulkUpdate({
      ids: selected,
      status,
      next_attempt_at: nextAttempt?.toISOString(),
    })
      .unwrap()
      .then(() => {
        setSelected([]);
        onClose();
        setNextAttempt(null);
        setStatus(undefined);
      })
      .catch((err) => setError(err.message));
  }, [bulkUpdate, nextAttempt, onClose, selected, setSelected, status]);

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Update orders
          </Typography>
          <IconButton onClick={onClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Alert severity="warning" sx={{ maxWidth: "400px" }}>
          <AlertTitle>{`You are about to update ${selected.length} order(s)`}</AlertTitle>
        </Alert>

        <FormControl fullWidth>
          <Typography>Status: </Typography>

          <TextField select value={status} onChange={handleChange}>
            {orderStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>

        <Stack gap={0}>
          <Typography>Next attempt at:</Typography>
          <DatePicker
            onChange={setNextAttempt}
            value={nextAttempt}
            minDate={new Date()}
          />
        </Stack>

        <Stack gap={1}>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error}
            </Typography>
          ) : null}

          <LoadingButton loading={isLoading} onClick={handleCancel}>
            Update
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default UpdateOrdersModal;
